import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAthleteMode } from "./AthleteModeContext";
import { fetchAthleteThread, fetchAthleteThreads, sendAthleteMessage } from "./athleteServices";
import { athleteDealAction } from "./athleteUtils";
import { fmtDateTime, timeAgo } from "../../ForBrands/brandDealUtils";
import iconPaperclip from "../../../assets/brand/icon-paperclip.svg";
import { BackLink, Badge, Btn, Card, ErrorState, Overline } from "../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import { InboxMobile, ThreadMobile } from "./mobile/MessagesMobile";

// Athlete Mode · Messages (Figma "Messages"): every brand conversation in
// one inbox. The athlete's rep (from their Circle) has visibility.
// Tablets and desktops show the split view and open the newest conversation.
// Phones (the Athlete Mode phone theme) get two screens from
// mobile/MessagesMobile: the inbox, where nothing is opened for the athlete,
// and the thread (?thread=), whose back button returns to the inbox.
// Each conversation keeps its own reply draft and attachment, so what was
// typed to one brand never shows up in another brand's composer.

const NO_DRAFT = Object.freeze({ text: "", attachment: null });

const Bubble = ({ message, brandName }) => {
  const mine = message.from === "athlete";
  return (
    <div className={`flex flex-col gap-[6px] ${mine ? "items-start" : "items-end"}`}>
      <div
        className={`max-w-[520px] px-4 py-3 bm-body-m text-bm-ink ${
          mine ? "rounded-[12px] rounded-bl-[4px] border border-bm-border bg-white" : "rounded-[12px] rounded-br-[4px] bg-bm-mutedbg"
        }`}
      >
        {message.text}
        {message.attachment?.name && (
          <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-bm-border bg-white px-3 py-1 bm-label-xs text-bm-ink">
            <img src={iconPaperclip} alt="" className="h-3 w-3" />
            {message.attachment.name}
          </div>
        )}
      </div>
      <span className="bm-body-s text-bm-muted">
        {mine ? "You" : brandName} · {fmtDateTime(message.at)}
      </span>
    </div>
  );
};

const AthleteMessagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { refreshBadges } = useAthleteMode();
  const fileRef = useRef(null);
  const viewportRef = useRef(null);

  const [threads, setThreads] = useState([]);
  const [rep, setRep] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState(null);
  // Reply drafts by conversation id: { text, attachment }.
  const [drafts, setDrafts] = useState({});
  // The conversation whose message is on its way.
  const [sendingId, setSendingId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [threadError, setThreadError] = useState(null);
  const threadId = params.get("thread");
  const phone = usePhoneTheme();
  // Read by the loaders, whose 30s poll keeps an older closure.
  const phoneRef = useRef(phone);
  // The open conversation, for loads that finish after the athlete moved on.
  const threadRef = useRef(threadId);
  threadRef.current = threadId;

  const { text, attachment } = (threadId && drafts[threadId]) || NO_DRAFT;
  const sending = sendingId !== null && sendingId === threadId;
  const updateDraft = (id, patch) => {
    if (!id) return;
    setDrafts((prev) => ({ ...prev, [id]: { ...NO_DRAFT, ...prev[id], ...patch } }));
  };
  const setText = (value) => updateDraft(threadId, { text: value });
  const setAttachment = (value) => updateDraft(threadId, { attachment: value });
  // No in-app page before this one: the first entry, or the inbox that
  // replaced a thread opened on the first entry (it carries `root`).
  const atRoot = location.key === "default" || Boolean(location.state?.root);

  const loadThreads = async () => {
    try {
      const data = await fetchAthleteThreads();
      setThreads(data.threads || []);
      setRep(data.rep || null);
      // The phone inbox is a screen of its own: nothing is opened for the athlete.
      if (!threadId && data.threads?.length && !phoneRef.current) setParams({ thread: data.threads[0].id }, { replace: true });
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load conversations");
    } finally {
      setLoaded(true);
    }
  };

  const loadThread = async (id) => {
    if (!id) return;
    try {
      const data = await fetchAthleteThread(id);
      // A conversation the athlete already left must not replace the open one.
      if (threadRef.current === id) {
        setActive(data);
        setThreadError(null);
      }
      setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, unread: 0 } : t)));
      refreshBadges();
    } catch (err) {
      toast.error(err.message || "Could not open the conversation");
      if (threadRef.current === id) setThreadError({ id, message: err.message || "Could not open the conversation" });
    }
  };

  useEffect(() => {
    phoneRef.current = phone;
  }, [phone]);

  // The split view always has a conversation open once there are any. With
  // no ?thread (the phone inbox turned to landscape, or the Messages link
  // followed from a conversation) it opens the one that was open last, else
  // the newest, instead of waiting for the next refresh.
  useEffect(() => {
    if (phone || threadId || !threads.length) return;
    const current = active && threads.some((t) => t.id === active.id) ? active.id : threads[0].id;
    setParams({ thread: current }, { replace: true, state: atRoot ? { root: true } : undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, threadId, threads]);

  useEffect(() => {
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadThread(threadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadThreads();
      if (threadId) loadThread(threadId);
    }, 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => {
    if (viewportRef.current) viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
  }, [active?.messages?.length]);

  // Sends to the conversation it was written in, and clears only that draft.
  const submit = async () => {
    const id = threadId;
    if (!id || (!text.trim() && !attachment)) return;
    setSendingId(id);
    try {
      await sendAthleteMessage(id, text.trim(), attachment);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await loadThread(id);
      await loadThreads();
    } catch (err) {
      toast.error(err.message || "Could not send the message");
    } finally {
      setSendingId((cur) => (cur === id ? null : cur));
    }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setAttachment({ name: f.name, size: f.size });
    e.target.value = "";
  };

  const brandName = active?.brand?.company || "Brand";
  const dealAction = active?.deal ? athleteDealAction(active.deal) : null;

  if (phone) {
    if (!threadId) {
      // Back leaves Messages the way the athlete came, else goes home.
      return (
        <InboxMobile
          threads={threads}
          rep={rep}
          error={error}
          loaded={loaded}
          onOpen={(id) => setParams({ thread: id }, { state: { fromInbox: true } })}
          onBack={() => (atRoot ? navigate("/athlete/home") : navigate(-1))}
        />
      );
    }
    // Only the conversation for this id (not the one open before it).
    const thread = active && active.id === threadId ? active : null;
    const listed = threads.find((t) => t.id === threadId);
    return (
      <ThreadMobile
        title={thread ? brandName : listed?.brand?.company || ""}
        thread={thread}
        threadError={threadError?.id === threadId ? threadError.message : null}
        rep={rep}
        dealAction={thread ? dealAction : null}
        text={text}
        onTextChange={setText}
        onSubmit={submit}
        sending={sending}
        attachment={attachment}
        onRemoveAttachment={() => setAttachment(null)}
        fileRef={fileRef}
        onPickFile={pickFile}
        // Opened from the inbox: step back to it. Opened from elsewhere (a
        // deal's "Message" button, a link): show the inbox in its place.
        onBack={() =>
          location.state?.fromInbox ? navigate(-1) : setParams({}, { replace: true, state: atRoot ? { root: true } : undefined })
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-6">
      <BackLink to="/athlete/home">Back to dashboard</BackLink>

      <div className="flex flex-col gap-[6px]">
        <Overline>Deal flow</Overline>
        <h1 className="bm-h-l text-bm-ink">Messages</h1>
        <p className="bm-body-l text-bm-muted">
          All your brand conversations{rep ? ` · ${rep.name} (your rep) has visibility on every thread` : ""}
        </p>
      </div>

      {error && <ErrorState>{error}</ErrorState>}

      <Card padding="p-0" className="flex h-[680px] overflow-hidden">
        <div className="flex w-[300px] shrink-0 flex-col border-r border-bm-border bg-bm-subtle md:w-[360px]">
          <div className="border-b border-bm-mutedbg p-4 bm-h-xs text-bm-ink">Conversations</div>
          <div className="bm-scroll flex-1 overflow-y-auto">
            {threads.length === 0 && (
              <p className="p-4 bm-body-m text-bm-muted">
                No conversations yet. Asking a question on an offer or messaging a brand from My Deals starts one.
              </p>
            )}
            {threads.map((t) => {
              const selected = t.id === threadId;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setParams({ thread: t.id })}
                  className={`flex w-full flex-col gap-1 border-b p-4 text-left ${selected ? "border-bm-border bg-white" : "border-transparent hover:bg-white/60"}`}
                >
                  <span className="flex items-center justify-between">
                    <span className="bm-label-l text-bm-ink">{t.brand?.company || "Brand"}</span>
                    <span className="bm-body-s text-bm-tertiary">{t.lastAt ? `${timeAgo(t.lastAt, { short: true })} ago` : ""}</span>
                  </span>
                  {t.campaignName && <span className="bm-body-s text-bm-tertiary">{t.campaignName}</span>}
                  <span className={`line-clamp-2 bm-body-m ${t.unread ? "text-bm-ink" : "text-bm-muted"}`}>{t.preview || "No messages yet"}</span>
                  {t.unread > 0 && (
                    <span className="pt-1">
                      <Badge tone="brand">New</Badge>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          {active ? (
            <>
              <div className="flex items-center justify-between gap-3 border-b border-bm-border p-4">
                <span className="flex min-w-0 flex-col gap-[2px]">
                  <span className="bm-h-m text-bm-ink">{brandName}</span>
                  <span className="truncate bm-body-s text-bm-muted">
                    {[active.campaign?.name, active.brand?.fullName ? `${active.brand.fullName}, brand contact` : null, rep ? "your rep can see this thread" : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                {dealAction && (
                  <Btn variant="secondary" size="sm" onClick={() => navigate(dealAction.href)}>
                    View deal details
                  </Btn>
                )}
              </div>
              <div ref={viewportRef} className="bm-scroll flex flex-1 flex-col gap-4 overflow-y-auto p-6">
                {active.messages?.length ? (
                  active.messages.map((m) => <Bubble key={m.id} message={m} brandName={active.brand?.fullName || brandName} />)
                ) : (
                  <p className="bm-body-m text-bm-muted">Say hello to start the conversation.</p>
                )}
              </div>
              <div className="flex flex-col gap-3 border-t border-bm-border bg-bm-subtle p-4">
                <div className="flex items-center gap-3">
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), submit())}
                    placeholder={`Reply to ${active.brand?.firstName || brandName}...`}
                    className="bm-input flex-1"
                  />
                  <input ref={fileRef} type="file" className="hidden" onChange={pickFile} />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    aria-label="Attach a file"
                    title={attachment ? attachment.name : "Attach a file"}
                    className={`flex h-10 w-10 items-center justify-center rounded-full border ${attachment ? "border-bm-orange bg-white" : "border-bm-border bg-bm-subtle"}`}
                  >
                    <img src={iconPaperclip} alt="" className="h-4 w-4" />
                  </button>
                  <Btn variant="tertiary" onClick={submit} disabled={sending || (!text.trim() && !attachment)}>
                    {sending ? "Sending..." : "Send"}
                  </Btn>
                </div>
                {attachment && (
                  <span className="flex items-center gap-2 bm-body-s text-bm-muted">
                    Attaching {attachment.name}
                    <button type="button" onClick={() => setAttachment(null)} className="text-bm-critical">
                      remove
                    </button>
                  </span>
                )}
                <p className="bm-body-s text-bm-muted">Messages are delivered in-app. Brands also get an email notification for every new message.</p>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center bm-body-m text-bm-muted">
              {threads.length ? "Pick a conversation." : "No conversation selected."}
            </div>
          )}
        </div>
      </Card>

      <p className="bm-body-s text-bm-muted">Messages are delivered in-app. You also get an email notification for every new message from a brand.</p>
    </div>
  );
};

export default AthleteMessagesPage;
